import { BlockJustText } from '@ddg-frontend/blocks'
import { formatDate, getResource } from '../utils'
import { useProgramOtherSchedules, useUserSelfcare } from '../hooks'
import { useApp } from '../components/app-context'
import {
  RecordDot,
  RecordDots,
} from '../components/program-drawer-components/record-dots'
import { Channel, Schedule } from '../types'
import { ProgramDrawer } from '../components/program-drawer'
import { useEffect, useState } from 'react'
import { FormMessages } from '@ddg-frontend/inputs'
import { Link } from 'react-router-dom'
import { setMetaTag } from '../utils/setMetaTag'
import { decodeUserSelfcare } from '../utils/decode-user-selfcare-info'
const RecordingCard = ({
  recording,
  recordingType,
  channel,
  onProgramClick,
  id,
}: {
  recording: Schedule
  recordingType: string
  channel: Channel | undefined
  onProgramClick: (
    id: number | undefined,
    channel: Channel | undefined,
    date: string | undefined
  ) => void
  id: number | undefined
}) => {
  const imageSource = `https://cdn-er-images.online.meo.pt/eemstb/ImageHandler.ashx?progTitle=${recording.title}&chCallLetter=${channel?.callLetter}&profile=16_9&appSource=GuiaTV&width=300&stb=retina2x`

  useEffect(() => {
    document.title = `Guia TV`

    setMetaTag(
      'meta[name="description"]',
      'Encontre a programação dos canais de TV'
    )
    setMetaTag('meta[property="og:title"]', `Guia TV`)
    setMetaTag(
      'meta[property="og:description"]',
      'Encontre a programação dos canais de TV'
    )
  }, [])
  return (
    <button
      onClick={() => {
        onProgramClick(
          id,
          channel,
          recording.start.day.split('/').reverse().join('-')
        )
      }}
      type="button"
      key={recording.id}
      className="recording-card"
    >
      <img src={imageSource} alt={recording.title} />
      <div className="recording-card-container">
        <h2 className="recording-title no-translate">
          {recording.title} {recordingType === 'everyday' && <RecordDots />}
          {recordingType === 'once' && <RecordDot />}
        </h2>
        <p className="channel-title no-translate">{channel?.title}</p>
        <p>
          {formatDate(recording.start.date, new Date(), true)}{' '}
          {recording.start.time} - {recording.end.time}
        </p>
        <button
          type="button"
          className="btn-text btn-color-1 recording-card-button"
        >
          {getResource('Edit')}
        </button>
      </div>
    </button>
  )
}

const RecordingGroup = ({
  scheduledProgram,
  onProgramClick,
  channel,
  recordingType,
}: {
  scheduledProgram: Schedule
  onProgramClick: (
    id: number | undefined,
    channel: Channel | undefined,
    date: string | undefined
  ) => void
  channel: Channel | undefined
  recordingType: string
}) => {
  const { data: schedules } = useProgramOtherSchedules(
    scheduledProgram.programId.toString() || undefined
  )
  const episode = schedules?.find((episode) => {
    // Check if it's the same day
    if (episode.start.day !== scheduledProgram.start.day) {
      return false
    }

    const parseTime = (timeString: string) => {
      const [hours, minutes] = timeString.split(':').map(Number)
      return hours * 60 + minutes
    }

    const episodeStartMinutes = parseTime(episode.start.time)
    const episodeEndMinutes = parseTime(episode.end.time)
    const programStartMinutes = parseTime(scheduledProgram.start.time)
    const programEndMinutes = parseTime(scheduledProgram.end.time)

    const timeDifference = Math.abs(episodeStartMinutes - programStartMinutes)
    const endTimeDifference = Math.abs(episodeEndMinutes - programEndMinutes)

    return timeDifference <= 30 && endTimeDifference <= 30
  })
  const programId = episode?.id || Number(scheduledProgram.programId)
  if (!schedules) {
    return null
  }
  return (
    <RecordingCard
      id={programId}
      key={scheduledProgram.id}
      recording={scheduledProgram}
      recordingType={recordingType}
      channel={channel}
      onProgramClick={onProgramClick}
    />
  )
}

const UserRecordingsPage = () => {
  const { data: userSelfcare } = useUserSelfcare()
  const {
    channelData,
    isProgramRecording,
    userRecordings,
    isUserRecordingsLoading,
    isUserRecordingsError,
  } = useApp()
  const { alias, commercialOffer, contact } = decodeUserSelfcare(userSelfcare)
  const selectedBox = `${commercialOffer}  ${alias ? `- ${alias}` : ''}  ${
    contact ? `(${contact})` : ''
  }`

  const [selectedInfo, setSelectedInfo] = useState<
    | {
        programId: number | undefined
        channel: Channel | undefined
        date: string | undefined
      }
    | undefined
  >(undefined)
  const onProgramClick = (
    id: number | undefined,
    channel: Channel | undefined,
    date: string | undefined = undefined
  ) => {
    if (id) {
      const url = new URL(window.location.href)
      url.searchParams.set('programId', String(id))
      url.searchParams.set('channel', channel?.callLetter || '')
      url.searchParams.set('date', date || '')
      window.history.pushState({}, '', url.toString())
    } else {
      const url = new URL(window.location.href)
      url.searchParams.delete('programId')
      url.searchParams.delete('channel')
      url.searchParams.delete('date')
      window.history.pushState({}, '', url.toString())
    }
    setSelectedInfo({
      programId: id,
      channel: channel,
      date: date,
    })
  }
  const isUserAuthenticated =
    window.ISites.MEOGo.Authentication.isOauthAuthenticated()

  return (
    <div className="recordings-page">
      <BlockJustText
        content={{
          state: 'FULFILLED',
          data: [
            {
              id: '0',
              texts: {
                title: getResource('Page.Recordings.Title'),
                additionalText: isUserAuthenticated ? selectedBox : '',
                description: isUserAuthenticated
                  ? getResource('Page.Recordings.Description')
                  : '',
              },
              hyperlinks: null,
              itemTextColor: null,
              labels: null,
              stamp: null,
              countdown: null,
            },
          ],
        }}
        configs={{
          attributes: {},
          blockBackgroundColor: '',
          blockBackgroundImage: '',
          blockLine: '',
          blockMargin: '',
          blockTextAlignment: 'txt-align-center',
          blockTextColor: '',
          itemBackgroundColor: '',
          itemTextColor: '',
          itemTextAlignment: 'txt-align-center',
          mode: '',
          loadingRatio: '1:1',
          initialSelected: '',
          blockAnchor: '',
          extraClasses: '',
          collapsable: false,
          fullWidth: '',
          imagePosition: '',
          colNumber: '3',
          activeItem: '',
          textAreaAlignment: '',
          verticalTextAlignment: '',
          eventTracking: false,
          eventTrackingCategory: '',
          siteContext: 'meo',
        }}
      />
      <div className="recording-navigation">
        <Link
          to="/"
          className="btn-color-1 btn-text icon-seta-left btn-icon-left"
        >
          {getResource('TVGuide')}
        </Link>
        <a
          href={
            import.meta.env.VITE_MEOGO_BASE_URL +
            '/explore#automatic-recordings'
          } //done
          className="btn-color-1 btn-text icon-seta-right"
          target="_blank"
        >
          {getResource('AutomaticRecordings')}
        </a>
      </div>
      {!isUserAuthenticated && (
        <div
          data-type="login"
          className="form-message program-drawer-message login-message"
        >
          <div>
            <h6>{getResource('UserRecordings.NoAuth.Title')}</h6>
            <p>
              <span>{getResource('Login.Subtitle')}</span>
              <button
                type="button"
                onClick={() => window.ISites.MEOGo.Authentication.goToLogin()}
              >
                {getResource('Login.IDMEO')}
              </button>
              .
            </p>
          </div>
        </div>
      )}
      {isUserAuthenticated && (
        <>
          {isUserRecordingsLoading && <div className="block-loading" />}
          {!isUserRecordingsLoading &&
            userRecordings?.length === 0 &&
            !isUserRecordingsError && (
              <div className="no-results-message">
                <div>
                  <h6>{getResource('UserRecordings.NoRecordings.Title')}</h6>
                  <p>
                    {getResource('UserRecordings.NoRecordings.Description')}
                  </p>
                </div>
              </div>
            )}
          {isUserRecordingsError && (
            <FormMessages
              type={'error'}
              title="Ocorreu um erro a carregar as gravações"
              content="Por favor, tente novamente mais tarde."
              className="recordings-error"
            />
          )}
          <div className="recordings-container">
            {!isUserRecordingsLoading &&
              !!userRecordings &&
              userRecordings
                // ?.sort((a, b) => (a.start.utc > b.start.utc ? -1 : 1)) //TOOD: Check this
                .map((recording) =>
                  recording.scheduledPrograms.map((scheduledProgram) => (
                    <RecordingGroup
                      key={scheduledProgram.id}
                      scheduledProgram={scheduledProgram}
                      onProgramClick={onProgramClick}
                      channel={channelData?.find(
                        (channel) => channel.callLetter === recording.callLetter
                      )}
                      recordingType={isProgramRecording(recording.programId)}
                    />
                  ))
                )}
          </div>
          <ProgramDrawer
            programId={selectedInfo?.programId}
            onProgramClick={onProgramClick}
            channel={selectedInfo?.channel}
            date={selectedInfo?.date}
          />
        </>
      )}
    </div>
  )
}

export default UserRecordingsPage
